import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Mic, Sparkles, Square, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeProps } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { showToast } from '@/shared/ui/toast';

import { SectionCard } from './SectionCard';
import { useVoiceRecorder } from './useVoiceRecorder';
import {
  CLINICAL_VOICE_NOTES_QUERY,
  MARK_CLINICAL_VOICE_NOTE_APPLIED_MUTATION,
  idempotencyContext,
} from './clinical.gql';
import { transcribeVoiceNote, type VoiceNoteSuggestions } from './voice-notes-rest';

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call */

// Only fields the sibling sections actually render + save — applying a suggestion for a
// field with no visible input would silently change the record with no on-screen trace.
// Split into 2 groups matching the 2-card "Historia Clínica" / "Anamnesis" form layout.
const HISTORIA_FIELDS = [
  'vaccination_status',
  'deworming_status',
  'surgeries_notes',
  'last_heat_notes',
  'diet_notes',
  'barf_diet_notes',
  'fleas_ticks_notes',
  'cohabitation_notes',
  'street_exposure_notes',
] as const;
const ANAM_FIELDS = ['chief_complaint', 'symptom_onset_days', 'other_notes'] as const;
const EXAM_FIELDS = [
  'body_weight_grams',
  'body_temperature_celsius_tenths',
  'heart_rate_bpm',
  'respiratory_rate_bpm',
  'body_condition_score',
  'mucous_membranes',
  'hydration_status',
  'consciousness_level',
  'cardiovascular_notes',
  'respiratory_notes',
  'abdomen_notes',
  'lymph_node_notes',
  'capillary_refill_time',
  'cough_reflex',
  'gag_reflex',
  'pulse_description',
  'auscultation_percussion_notes',
] as const;
const PLAN_FIELDS = [
  'treatment_notes',
  'medications_notes',
  'diet_recommendations',
  'activity_restrictions',
  'follow_up_notes',
  'owner_instructions',
] as const;

const STATUS_VARIANT: Record<string, NonNullable<BadgeProps['variant']>> = {
  succeeded: 'success',
  partial: 'warning',
  processing: 'muted',
  failed: 'destructive',
  dead: 'destructive',
};

function errCode(err: unknown): string {
  if (err instanceof ApolloError) return err.graphQLErrors[0]?.message ?? err.message;
  if (err instanceof Error) return err.message;
  return 'error';
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

interface VoiceFillSectionProps {
  visitId: string;
  canUse: boolean;
  writable: boolean;
  onApplyAnamnesis: (patch: Record<string, string>) => void;
  onApplyExam: (patch: Record<string, string>) => void;
  onApplyPlan: (patch: Record<string, string>) => void;
  onApplyDiagnosis: (d: { description: string; diagnosis_kind: string; is_primary: boolean }) => void;
  /** Applies + persists every suggested section (anamnesis/exam/plan/diagnoses) in one shot.
   *  Resolves `false` (without throwing) when the save failed, so the note isn't marked applied. */
  onApplyAndSaveAll: (suggestions: VoiceNoteSuggestions) => Promise<boolean>;
}

export function VoiceFillSection({
  visitId,
  canUse,
  writable,
  onApplyAnamnesis,
  onApplyExam,
  onApplyPlan,
  onApplyDiagnosis,
  onApplyAndSaveAll,
}: VoiceFillSectionProps): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery(CLINICAL_VOICE_NOTES_QUERY, {
    variables: { visit_id: visitId },
    skip: !visitId,
    fetchPolicy: 'cache-and-network',
  });
  const notes: any[] = data?.clinicalVoiceNotes ?? [];

  const recorder = useVoiceRecorder();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [applyingAllId, setApplyingAllId] = useState<string | null>(null);

  const [markApplied] = useMutation(MARK_CLINICAL_VOICE_NOTE_APPLIED_MUTATION);

  const canRecord = canUse && writable;

  const runTranscribe = async (blob: Blob, filename: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const result = await transcribeVoiceNote(visitId, blob, filename);
      const refreshed = await refetch();
      const fresh = (refreshed.data?.clinicalVoiceNotes ?? []).find((n: any) => n.id === result.id);
      setExpandedId(fresh?.id ?? null);
      if (result.status === 'succeeded') {
        showToast({ tone: 'success', title: t('clinical.voice_toast_transcribed') });
      } else if (result.status === 'partial') {
        showToast({ tone: 'info', title: t('clinical.voice_toast_partial') });
      } else if (result.status === 'processing') {
        // Skald returned queued/running despite `?wait=true` — per the clinical-tenant
        // contract this should never happen (no async delivery exists here to catch it
        // later). Almost always a Skald-side provisioning issue (schema not published /
        // tenant not yet provisioned clinical), not something retrying fixes.
        setError(
          t('clinical.voice_error_stuck_processing', {
            request_id: result.skald_request_id ?? result.id,
          }),
        );
      } else {
        // `status` dead/failed comes back as a normal 200 (the row was still persisted) —
        // surface Skald's actual (already-redacted, PHI-safe) message instead of a generic
        // "try again" that hides *why* it failed.
        setError(result.error_message ?? t('clinical.voice_error_processing'));
      }
    } catch (e) {
      const code = errCode(e);
      const known = ['feature_disabled', 'clinical_voice_note_too_large', 'clinical_visit_locked'];
      setError(known.includes(code) ? t(`clinical.error_${code}`) : t('clinical.voice_error_processing'));
    } finally {
      setBusy(false);
      setFile(null);
    }
  };

  const onToggleRecord = async (): Promise<void> => {
    if (recorder.recording) {
      const blob = await recorder.stop();
      if (blob) await runTranscribe(blob, `visit-${visitId}.webm`);
      return;
    }
    await recorder.start();
  };

  const onUploadFile = async (): Promise<void> => {
    if (!file) return;
    await runTranscribe(file, file.name);
  };

  const onApplyField = (
    section: 'anamnesis' | 'physical_exam' | 'plan',
    note_id: string,
    apply: () => void,
  ): void => {
    apply();
    markApplied({
      variables: { input: { id: note_id, sections: [section] } },
      context: idempotencyContext(),
    }).catch(() => undefined);
  };

  const onApplyDiag = (
    note_id: string,
    d: { description: string; diagnosis_kind: string; is_primary: boolean },
  ): void => {
    onApplyDiagnosis(d);
    markApplied({
      variables: { input: { id: note_id, sections: ['diagnoses'] } },
      context: idempotencyContext(),
    }).catch(() => undefined);
  };

  const onApplyAllForNote = async (note_id: string, s: VoiceNoteSuggestions): Promise<void> => {
    setApplyingAllId(note_id);
    try {
      const saved = await onApplyAndSaveAll(s);
      if (!saved) return;
      const sections: string[] = [];
      if (Object.keys(s.anamnesis).length > 0) sections.push('anamnesis');
      if (Object.keys(s.physical_exam).length > 0) sections.push('physical_exam');
      if (Object.keys(s.plan).length > 0) sections.push('plan');
      if (s.diagnoses.length > 0) sections.push('diagnoses');
      if (sections.length > 0) {
        await markApplied({
          variables: { input: { id: note_id, sections } },
          context: idempotencyContext(),
        }).catch(() => undefined);
        await refetch();
      }
    } finally {
      setApplyingAllId(null);
    }
  };

  return (
    <div className="space-y-3">
      {error ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
          {error}
        </div>
      ) : null}

      {canRecord ? (
        <SectionCard icon={Mic} title={t('clinical.voice_card_title')}>
          <p className="text-muted-foreground text-xs">{t('clinical.voice_card_hint')}</p>
          <p className="text-muted-foreground text-xs italic">
            {t('clinical.voice_card_history_reminder')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant={recorder.recording ? 'destructive' : 'default'}
              onClick={() => void onToggleRecord()}
              disabled={busy}
            >
              {recorder.recording ? (
                <>
                  <Square className="mr-1 h-4 w-4" aria-hidden />
                  {t('clinical.voice_stop_recording')} · {formatDuration(recorder.duration_seconds)}
                </>
              ) : (
                <>
                  <Mic className="mr-1 h-4 w-4" aria-hidden />
                  {t('clinical.voice_start_recording')}
                </>
              )}
            </Button>
            <span className="text-muted-foreground text-xs">{t('clinical.voice_or')}</span>
            <input
              type="file"
              accept="audio/*"
              className="text-sm"
              disabled={busy || recorder.recording}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy || !file || recorder.recording}
              onClick={() => void onUploadFile()}
            >
              <Upload className="mr-1 h-4 w-4" aria-hidden />
              {busy ? t('common.loading') : t('clinical.voice_transcribe')}
            </Button>
          </div>
          {recorder.error ? (
            <p className="text-destructive text-xs">{t(`clinical.error_${recorder.error}`)}</p>
          ) : null}
        </SectionCard>
      ) : null}

      {notes.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('clinical.voice_no_notes')}</p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const expanded = expandedId === n.id;
            const suggestions: VoiceNoteSuggestions | null = n.suggestions_json
              ? (JSON.parse(n.suggestions_json) as VoiceNoteSuggestions)
              : null;
            return (
              <div key={n.id} className="space-y-2 rounded-md border p-3">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 text-left"
                  onClick={() => setExpandedId(expanded ? null : n.id)}
                >
                  <span className="flex items-center gap-2">
                    <Sparkles className="text-primary h-4 w-4" aria-hidden />
                    <span className="text-sm font-medium">
                      {new Date(n.created_at).toLocaleString()}
                    </span>
                    {n.audio_seconds != null ? (
                      <span className="text-muted-foreground text-xs">
                        {formatDuration(n.audio_seconds)}
                      </span>
                    ) : null}
                  </span>
                  <Badge variant={STATUS_VARIANT[n.status] ?? 'muted'}>
                    {t(`clinical.voice_status_${n.status}`)}
                  </Badge>
                </button>

                {expanded ? (
                  <div className="space-y-3 border-t pt-2">
                    {n.status === 'processing' ? (
                      <p className="text-destructive text-xs">
                        {t('clinical.voice_error_stuck_processing', {
                          request_id: n.skald_request_id ?? n.id,
                        })}
                      </p>
                    ) : null}
                    {n.status === 'dead' ? (
                      <p className="text-destructive text-xs">
                        {t('clinical.voice_error_dead')}
                        {n.error_message ? ` (${n.error_message})` : ''}
                      </p>
                    ) : null}
                    {n.status === 'failed' ? (
                      <p className="text-destructive text-xs">
                        {t('clinical.voice_error_failed_retry')}
                        {n.error_message ? ` (${n.error_message})` : ''}
                      </p>
                    ) : null}
                    {n.status === 'partial' ? (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t('clinical.voice_partial_hint')}
                      </p>
                    ) : null}

                    {n.transcript_text ? (
                      <div className="bg-muted/40 rounded-md p-2 text-sm whitespace-pre-wrap">
                        {n.transcript_text}
                      </div>
                    ) : null}

                    {n.status === 'succeeded' && suggestions ? (
                      <div className="space-y-3">
                        <div className="bg-muted/30 flex items-center justify-between gap-2 rounded-md border p-2">
                          <p className="text-muted-foreground text-xs">
                            {t('clinical.voice_apply_all_hint')}
                          </p>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => void onApplyAllForNote(n.id, suggestions)}
                            disabled={applyingAllId === n.id}
                          >
                            {applyingAllId === n.id ? t('common.loading') : t('clinical.voice_apply_all')}
                          </Button>
                        </div>

                        {HISTORIA_FIELDS.some((k) => suggestions.anamnesis[k] !== undefined) ? (
                          <SuggestionGroup
                            title={t('clinical.card_history')}
                            entries={HISTORIA_FIELDS.filter(
                              (k) => suggestions.anamnesis[k] !== undefined,
                            ).map((k) => ({
                              key: k,
                              label: t(`clinical.f_${k}`),
                              value: String((suggestions.anamnesis as any)[k]),
                            }))}
                            onApplyAll={() =>
                              onApplyField('anamnesis', n.id, () =>
                                onApplyAnamnesis(
                                  stringifyPatch(pickFields(suggestions.anamnesis, HISTORIA_FIELDS)),
                                ),
                              )
                            }
                            applyAllLabel={t('clinical.voice_apply_section')}
                          />
                        ) : null}

                        {ANAM_FIELDS.some((k) => suggestions.anamnesis[k] !== undefined) ? (
                          <SuggestionGroup
                            title={t('clinical.card_reason_and_evolution')}
                            entries={ANAM_FIELDS.filter((k) => suggestions.anamnesis[k] !== undefined).map(
                              (k) => ({
                                key: k,
                                label: t(`clinical.f_${k}`),
                                value: String((suggestions.anamnesis as any)[k]),
                              }),
                            )}
                            onApplyAll={() =>
                              onApplyField('anamnesis', n.id, () =>
                                onApplyAnamnesis(
                                  stringifyPatch(pickFields(suggestions.anamnesis, ANAM_FIELDS)),
                                ),
                              )
                            }
                            applyAllLabel={t('clinical.voice_apply_section')}
                          />
                        ) : null}

                        {EXAM_FIELDS.some((k) => (suggestions.physical_exam as any)[k] !== undefined) ? (
                          <SuggestionGroup
                            title={t('clinical.card_vitals')}
                            entries={EXAM_FIELDS.filter(
                              (k) => (suggestions.physical_exam as any)[k] !== undefined,
                            ).map((k) => ({
                              key: k,
                              label: t(`clinical.f_${k}`),
                              value: String((suggestions.physical_exam as any)[k]),
                              inferred: (suggestions.physical_exam_inferred_fields ?? []).includes(k),
                            }))}
                            onApplyAll={() =>
                              onApplyField('physical_exam', n.id, () =>
                                onApplyExam(stringifyPatch(suggestions.physical_exam)),
                              )
                            }
                            applyAllLabel={t('clinical.voice_apply_section')}
                          />
                        ) : null}

                        {PLAN_FIELDS.some((k) => (suggestions.plan as any)[k] !== undefined) ? (
                          <SuggestionGroup
                            title={t('clinical.card_treatment')}
                            entries={PLAN_FIELDS.filter(
                              (k) => (suggestions.plan as any)[k] !== undefined,
                            ).map((k) => ({
                              key: k,
                              label: t(`clinical.f_${k}`),
                              value: String((suggestions.plan as any)[k]),
                            }))}
                            onApplyAll={() =>
                              onApplyField('plan', n.id, () => onApplyPlan(stringifyPatch(suggestions.plan)))
                            }
                            applyAllLabel={t('clinical.voice_apply_section')}
                          />
                        ) : null}

                        {suggestions.diagnoses.length > 0 ? (
                          <div className="space-y-1.5 rounded-md border p-2">
                            <p className="text-sm font-medium">{t('clinical.tab_diagnoses')}</p>
                            {suggestions.diagnoses.map((d, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between gap-2 rounded-md border px-2 py-1.5"
                              >
                                <div className="min-w-0">
                                  <p className="truncate text-sm">{d.description}</p>
                                  <Badge variant="outline">
                                    {t(`clinical.diagnosis_kind_${d.diagnosis_kind}`)}
                                  </Badge>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => onApplyDiag(n.id, d)}>
                                  {t('clinical.voice_use_suggestion')}
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Scopes a suggestion object down to the keys a given SuggestionGroup actually displays —
// without this, "Aplicar" on one card (e.g. Historia Clínica) would silently also write
// fields shown only in a sibling card (e.g. Motivo y evolución), since both groups read
// from the same `suggestions.anamnesis` object.
function pickFields<T extends object, K extends readonly (keyof T)[]>(obj: T, keys: K): Partial<T> {
  const out: Partial<T> = {};
  for (const key of keys) {
    if (obj[key] !== undefined) out[key] = obj[key];
  }
  return out;
}

export function stringifyPatch<T extends object>(patch: T): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (v !== undefined && v !== null) out[k] = String(v);
  }
  return out;
}

function SuggestionGroup({
  title,
  entries,
  onApplyAll,
  applyAllLabel,
}: {
  title: string;
  entries: { key: string; label: string; value: string; inferred?: boolean }[];
  onApplyAll: () => void;
  applyAllLabel: string;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5 rounded-md border p-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{title}</p>
        <Button size="sm" variant="outline" onClick={onApplyAll}>
          {applyAllLabel}
        </Button>
      </div>
      <ul className="space-y-1">
        {entries.map((e) => (
          <li key={e.key} className="text-sm">
            <span className="text-muted-foreground">{e.label}: </span>
            <span>{e.value}</span>
            {e.inferred ? (
              <Badge variant="outline" className="ml-1.5 align-middle text-[10px]">
                {t('clinical.voice_inferred_normal')}
              </Badge>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
